import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import ContractInfoList, {
  buildContractContentHtml,
} from '@/components/Approval/@components/ContractInfo';
import type { ElawContract } from '@/types/approval';
import { FieldBlock, FieldHeader } from '../styles';
import type { ApprovalFormValues } from '../types';
import AiDraftStatusList from './AiDraftStatusList';
import ContractPicker from './ContractPicker';
import { AiDraftStatusRow, InlineMessage, LinkedList } from './styles';
import useContractLinkField, {
  describeDraftSourceError,
} from './useContractLinkField';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';

type ContractLinkFieldProps = {
  apvType: number;
  /**
   * 폼이 비어 있을 때 첫 등록분으로 자동 채울 계약 ID(순서 유지).
   * 편집 진입 = 단건조회 attributes.contractIds, 법틀 진입 = URL contractId
   */
  initialContractIds?: number[];
  /** initialContractIds 가 접근 불가(404)면 폼에서 빼고 스낵바 안내 — 법틀 진입(신규 작성) 전용 */
  discardInitialOnForbidden?: boolean;
  /** 신규 작성(법틀 진입)만 true — 들고 온 계약의 AI 초안을 바로 생성. 편집은 본문에 이미 있음 */
  generateInitialDrafts?: boolean;
};

/**
 * 계약 품의(117·118) 계약 연결 슬롯 — 본문 위. 선택 + 등록 → 누적.
 * 표시는 조회 슬롯과 같은 ContractInfoList(계약마다 같은 묶음, 모두 삭제 가능)
 */
const ContractLinkField = ({
  apvType,
  initialContractIds,
  discardInitialOnForbidden,
  generateInitialDrafts,
}: ContractLinkFieldProps) => {
  const { t } = useTranslation();
  const {
    ids,
    linked,
    pendingId,
    setPendingId,
    register,
    remove,
    available,
    restored,
    draft,
  } = useContractLinkField(apvType, initialContractIds, {
    discardInitialOnForbidden,
    generateInitialDrafts,
  });

  const resolved = linked
    .map((item) => item.contract)
    .filter((contract): contract is ElawContract => Boolean(contract));
  // 한 건이라도 메타가 없으면 목록을 그리지 않음 — 순서가 어긋나 보이지 않게
  const allResolved = resolved.length === linked.length;

  const { setValue } = useFormContext<ApprovalFormValues>();
  const resolvedKey = allResolved ? JSON.stringify(resolved) : null;
  // 계약 정보를 다 받기 전에는 undefined — 편집 진입 때 넣어 둔 저장값을 비우지 않음
  const contractContent = useMemo(() => {
    if (!linked.length) return null;
    if (!resolvedKey) return undefined;
    return buildContractContentHtml(JSON.parse(resolvedKey), t);
  }, [linked.length, resolvedKey, t]);
  useEffect(() => {
    if (contractContent === undefined) return;
    setValue('contractContent', contractContent);
  }, [contractContent, setValue]);

  const renderLinked = () => {
    if (!linked.length) return null;
    if (allResolved) {
      return (
        <ContractInfoList
          contracts={resolved}
          onRemove={remove}
          renderFooter={(contractId) => (
            <AiDraftStatusList
              contractIds={[contractId]}
              statuses={draft.statuses}
              resolveTitle={draft.resolveTitle}
              onRetry={draft.retry}
            />
          )}
        />
      );
    }
    if (restored.isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('계약 정보를 불러오는 중입니다…')}
        </ApprovalNoticeBox>
      );
    }
    return (
      <ApprovalNoticeBox role="alert">
        <AiDraftStatusRow>
          <span>{describeDraftSourceError(restored.errorKind, t)}</span>
          <BoxButton
            variant="secondary"
            size="small"
            onClick={() => restored.mutate()}
          >
            {t('다시 시도')}
          </BoxButton>
        </AiDraftStatusRow>
      </ApprovalNoticeBox>
    );
  };

  return (
    <FieldBlock>
      <FieldHeader>
        <InfoSectionTitle>{t('계약 검토이력')}</InfoSectionTitle>
      </FieldHeader>
      <ContractPicker
        contracts={available.contracts ?? []}
        registeredIds={ids}
        value={pendingId}
        onChange={setPendingId}
        onRegister={register}
        isLoading={available.isLoading}
        disabled={Boolean(available.error)}
      />
      {available.error && (
        <InlineMessage role="alert">
          <Body2 component="span">
            {describeDraftSourceError(available.errorKind, t)}
          </Body2>
          <BoxButton
            variant="secondary"
            size="small"
            onClick={() => available.mutate()}
          >
            {t('다시 시도')}
          </BoxButton>
        </InlineMessage>
      )}
      <LinkedList>{renderLinked()}</LinkedList>
      {!allResolved && (
        <LinkedList>
          <AiDraftStatusList
            contractIds={ids}
            statuses={draft.statuses}
            resolveTitle={draft.resolveTitle}
            onRetry={draft.retry}
          />
        </LinkedList>
      )}
    </FieldBlock>
  );
};

export default ContractLinkField;
